import { createTheme } from "@mui/material";
import { mergeDeep } from "@parallelpublicworks/clf-utils";
import {
	cloverleafTheme,
	cloverleafParentTheme,
} from "@parallelpublicworks/gatsby-clf-material/themes";
import getThemePalette from "./getThemePalette";

function getTheme(config = {}) {
	const breakpoints = {
		values: {
			xs: 0,
			sm: 960,
			md: 1200,
			lg: 1350,
			xl: 1420,
		},
	};
	const defaultTheme = createTheme({
		breakpoints,
	});

	const palette = getThemePalette(defaultTheme);

	const containerMaxWidths = {
		xs: "100%",
		sm: "880px",
		md: "1120px",
		lg: "1260px",
		xl: "1380px",
	};

	const cloverleafBaseConfig = mergeDeep(cloverleafParentTheme, cloverleafTheme);

	const baseConfig = mergeDeep(cloverleafBaseConfig, config);

	const defaultConfig = {
		breakpoints,
		palette,
		shape: {
			borderRadius: 3,
		},
		typography: {
			fontWeight: 400,
			fontWeightBold: 600,
			fontSize: 16,
			fontSizeLg: 18,
			fontSizeSm: 14,
			fontSizeXs: 12,
			h1: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "2.6rem",
				[defaultTheme.breakpoints.down("md")]: {
					fontSize: "2rem",
				},
				[defaultTheme.breakpoints.down("sm")]: {
					fontSize: "1.7rem",
				},
			},
			h2: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "2rem",
				[defaultTheme.breakpoints.down("md")]: {
					fontSize: "1.6rem",
				},
				[defaultTheme.breakpoints.down("sm")]: {
					fontSize: "1.4rem",
				},
			},
			h3: {
				fontWeight: 600,
				lineHeight: 1.267,
				letterSpacing: "-0.01562em",
				fontSize: "1.6rem",
				[defaultTheme.breakpoints.down("md")]: {
					fontSize: "1.3rem",
				},
				[defaultTheme.breakpoints.down("sm")]: {
					fontSize: "1.2rem",
				},
			},
			h4: {
				fontWeight: 600,
				lineHeight: 1.267,
				letterSpacing: "-0.01562em",
				fontSize: "1.3rem",
				[defaultTheme.breakpoints.down("md")]: {
					fontSize: "1.2rem",
				},
			},
			h5: {
				fontWeight: 600,
				lineHeight: 1.367,
				letterSpacing: "-0.01562em",
				fontSize: "1rem",
			},
			h6: {
				fontWeight: 600,
				lineHeight: 1.467,
				letterSpacing: "-0.01562em",
				fontSize: "0.8rem",
			},
			body2: {
				lineHeight: "1.5",
				opacity: "0.7",
			},
			button: {
				fontWeight: 600,
			},
		},
		components: {
			MuiContainer: {
				styleOverrides: {
					root: ({ ownerState }) => ({
						padding: baseConfig?.components?.MuiContainer?.styleOverrides?.root?.padding
							? baseConfig.components.MuiContainer.styleOverrides.root.padding
							: defaultTheme.spacing(3, 2),
						[defaultTheme.breakpoints.up("xs")]: {
							maxWidth: containerMaxWidths.xs,
						},
						[defaultTheme.breakpoints.up("sm")]: {
							maxWidth: containerMaxWidths.sm,
						},
						[defaultTheme.breakpoints.up("md")]: {
							maxWidth: containerMaxWidths.md,
						},
						[defaultTheme.breakpoints.up("lg")]: {
							maxWidth: containerMaxWidths.lg,
						},
						[defaultTheme.breakpoints.up("xl")]: {
							maxWidth: containerMaxWidths.xl,
						},
					}),
				},
			},
			MuiButton: {
				styleOverrides: {
					heading: {
						...defaultTheme.typography.h4,
						textTransform: "none",
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: (themeParam) => ({
					html: {
						backgroundColor: themeParam.palette.footer.dark,
						fontSize: themeParam.typography.fontSize,
					},
					body: {
						color: themeParam.palette.body.contrastText,
						fontSize: "1rem",
					},
					a: {
						color: themeParam.palette.link.main,
					},
					"#___gatsby": {
						backgroundColor: themeParam.palette.body.light,
					},
					"@keyframes fadeIn ": {
						from: {
							opacity: 0,
						},
					},
					".loading-pulse": {
						animation: "fadeIn 0.5s infinite alternate linear",
					},
					nav: {
						ul: {
							listStyle: "none",
							padding: 0,
							margin: 0,
							a: {
								display: "block",
								padding: themeParam.spacing(1, 0),
							},
						},
					},
				}),
			},
			MuiTypography: {
				defaultProps: {
					variantMapping: {
						h1: "h1",
						h2: "h2",
						h3: "h3",
						h4: "h4",
						h5: "h4",
						h6: "h4",
						subtitle1: "h4",
						subtitle2: "h4",
						body1: "p",
						body2: "p",
					},
				},
			},
			MuiFilledInput: {
				styleOverrides: {
					input: {
						// backgroundColor: defaultTheme.palette.background.paper,
					},
				},
			},
		},
		cloverleaf: {
			googleFont: "Roboto:ital,wght@0,400;0,700;1,400;1,500;1,700",
			navbar: {
				layout: "bar",
			},
			brand: {
				size: 140,
			},
		},
	};

	defaultConfig.typography.brand = { ...defaultTheme.typography.h5 };
	defaultConfig.typography.tagline = { ...defaultTheme.typography.caption };
	defaultConfig.typography.lead = {
		...defaultTheme.typography.h5,
		fontWeight: defaultTheme.typography.fontWeightLight,
	};

	const mergedConfig = mergeDeep(defaultConfig, baseConfig);

	const muitheme = createTheme(mergedConfig);
	if (config.googleFonts) {
		muitheme.cloverleaf.googleFont = config.googleFonts;
	}

	return muitheme;
}

export default getTheme;
